import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faDownload,
  faTrash,
  faEye,
  faPen,
} from '@fortawesome/free-solid-svg-icons';
import { Asset } from '@core/generated/gql-types';
import { getAssetChildAnswersSummary } from '@core/util/assetMetadata.util';
import type { DocumentSlot } from '@core/util/documentSlots.util';

interface DocumentSlotRowProps {
  slot: DocumentSlot;
  asset: Asset;
  uploadedBy: string;
  uploadedDate: string | null;
  fileName: string;
  onPreview: (asset: Asset, title: string) => void;
  onDownload: (asset: Asset) => void;
  onEditDetails: (slot: DocumentSlot, asset: Asset) => void;
  onReject: (asset: Asset) => void;
}

const DocumentSlotRow: React.FC<DocumentSlotRowProps> = ({
  slot,
  asset,
  uploadedBy,
  uploadedDate,
  fileName,
  onPreview,
  onDownload,
  onEditDetails,
  onReject,
}) => {
  const summaryItems = getAssetChildAnswersSummary(asset, slot.childQuestions);

  return (
    <div className="flex flex-col gap-3 border-b border-gray-100 pb-4 last:border-b-0 sm:flex-row sm:items-start sm:justify-between">
      <div className="flex-1">
        <div className="text-sm font-medium text-gray-900">{slot.title}</div>
        {uploadedDate && (
          <div className="mt-1 text-xs text-gray-500">
            {fileName} uploaded by {uploadedBy} on {uploadedDate}
          </div>
        )}
        {summaryItems.length > 0 && (
          <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-xs">
            {summaryItems.map((item) => (
              <span
                key={item.key}
                className={
                  item.isWarning || item.isExpired
                    ? 'text-red-600'
                    : 'text-gray-500'
                }
              >
                {item.label}
              </span>
            ))}
          </div>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => onPreview(asset, slot.title)}
          className="flex items-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          <FontAwesomeIcon icon={faEye} className="h-4 w-4" />
          Preview
        </button>
        <button
          type="button"
          onClick={() => void onDownload(asset)}
          className="flex items-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          <FontAwesomeIcon icon={faDownload} className="h-4 w-4" />
          Download
        </button>
        <button
          type="button"
          onClick={() => onEditDetails(slot, asset)}
          className="flex items-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          <FontAwesomeIcon icon={faPen} className="h-4 w-4" />
          Edit details
        </button>
        <button
          type="button"
          onClick={() => onReject(asset)}
          className="flex items-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          <FontAwesomeIcon icon={faTrash} className="h-4 w-4" />
          Reject
        </button>
      </div>
    </div>
  );
};

export default DocumentSlotRow;
